import { useCallback, useEffect, useMemo, useState } from "react";
import { IconArrowsVertical, IconCircleCheck, IconExternalLink } from "@tabler/icons-react";
import { axiosInstance } from "../../utils/axiosInstance";
import { OneUptimeOpenIncident } from "../../interfaces/interfaces";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  Pill,
  RefreshButton,
  Tone,
} from "../ui";

function formatWhen(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function severityTone(severity?: string | null): Tone {
  const s = String(severity || "").toLowerCase();
  if (s.includes("critical") || s.includes("high") || s.includes("major")) return "bad";
  if (s.includes("med") || s.includes("minor")) return "warn";
  return "neutral";
}

function sortByDeclared(
  list: OneUptimeOpenIncident[],
  ascending: boolean
): OneUptimeOpenIncident[] {
  return list.slice().sort((a, b) => {
    const ta = a.declaredAt ? new Date(String(a.declaredAt)).getTime() : 0;
    const tb = b.declaredAt ? new Date(String(b.declaredAt)).getTime() : 0;
    if (ta !== tb) return ascending ? ta - tb : tb - ta;
    return String(a.id).localeCompare(String(b.id));
  });
}

export default function OpenIncidentsHome() {
  const [incidents, setIncidents] = useState<OneUptimeOpenIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [dateAscending, setDateAscending] = useState(false);
  const [severityFilter, setSeverityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await axiosInstance.get<{
        incidents: OneUptimeOpenIncident[];
        message?: string;
      }>("/monitoring/open-incidents");
      const list = res.data.incidents || [];
      setIncidents(list);
      if (res.data.message && list.length === 0) {
        setInfo(res.data.message);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      if (status === 400 && Array.isArray(data?.incidents)) {
        setIncidents(data.incidents);
        setInfo(data.message || null);
        setError(null);
      } else if (status === 503 && Array.isArray(data?.incidents)) {
        setIncidents(data.incidents);
        setInfo(data.message || null);
        setError(null);
      } else {
        const msg =
          data?.message ||
          err?.message ||
          "Failed to load incidents.";
        setError(msg);
        setIncidents([]);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const severityOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of incidents) {
      if (row.severity) set.add(String(row.severity));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [incidents]);

  const stateOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of incidents) {
      if (row.state) set.add(String(row.state));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [incidents]);

  const display = useMemo(() => {
    const filtered = incidents.filter((row) => {
      if (severityFilter && String(row.severity ?? "") !== severityFilter) {
        return false;
      }
      if (stateFilter && String(row.state ?? "") !== stateFilter) return false;
      return true;
    });
    return sortByDeclared(filtered, dateAscending);
  }, [incidents, severityFilter, stateFilter, dateAscending]);

  const filtersActive = Boolean(severityFilter || stateFilter);

  return (
    <PageContainer>
      <PageHeader
        title="Open incidents"
        subtitle="Active incidents from OneUptime"
        actions={<RefreshButton onClick={load} loading={loading} />}
      />

      {!loading && error ? <ErrorBanner onRetry={load}>{error}</ErrorBanner> : null}
      {!loading && !error && info && incidents.length === 0 ? (
        <div className="alert alert-info" role="alert">
          {info}
        </div>
      ) : null}

      <Card flush>
        {!loading && !error && incidents.length > 0 ? (
          <div className="sq-toolbar">
            <label className="sq-field is-half">
              <span>Severity</span>
              <select
                className="sq-input"
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
              >
                <option value="">All</option>
                {severityOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <label className="sq-field is-half">
              <span>State</span>
              <select
                className="sq-input"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              >
                <option value="">All</option>
                {stateOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <span className="sq-count sq-toolbar-end">
              Showing {display.length} of {incidents.length}
            </span>
          </div>
        ) : null}

        {loading ? <LoadingPanel label="Loading incidents" /> : null}

        {!loading && !error && incidents.length === 0 && !info ? (
          <EmptyState icon={<IconCircleCheck size={24} />} title="No open incidents" />
        ) : null}

        {!loading && !error && incidents.length > 0 && display.length === 0 ? (
          <EmptyState
            title="No incidents match these filters"
            action={
              filtersActive ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setSeverityFilter("");
                    setStateFilter("");
                  }}
                >
                  Clear filters
                </button>
              ) : undefined
            }
          />
        ) : null}

        {!loading && !error && display.length > 0 ? (
          <div className="sq-table-wrap">
            <table className="sq-table is-stacked">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Title</th>
                  <th scope="col">State</th>
                  <th scope="col">Severity</th>
                  <th scope="col" aria-sort={dateAscending ? "ascending" : "descending"}>
                    <button
                      type="button"
                      className="sortable"
                      onClick={() => setDateAscending((prev) => !prev)}
                    >
                      Declared
                      <IconArrowsVertical size={14} stroke={1.75} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {display.map((row) => {
                  const href =
                    typeof row.externalUrl === "string" && row.externalUrl ? row.externalUrl : null;
                  return (
                    <tr key={row.id}>
                      <td data-label="ID" className="nowrap">
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sq-text-link d-inline-flex align-items-center gap-1"
                            title="Open in OneUptime"
                          >
                            {row.id}
                            <IconExternalLink size={14} stroke={1.75} />
                          </a>
                        ) : (
                          row.id
                        )}
                      </td>
                      <td className="cell-title sq-strong">
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sq-text-link"
                            title="Open in OneUptime"
                          >
                            {row.title}
                          </a>
                        ) : (
                          row.title
                        )}
                      </td>
                      <td data-label="State">{row.state || "—"}</td>
                      <td data-label="Severity">
                        {row.severity ? (
                          <Pill tone={severityTone(row.severity)}>{row.severity}</Pill>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td data-label="Declared">{formatWhen(row.declaredAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </PageContainer>
  );
}
